import { defineComponent as _defineComponent } from 'vue'
import { queries } from "@/apollo";
import {
  User,
  CompanyReport,
  StatementType,
  ExternalCompany,
} from "@/CodegenTypes";
import type { LegacyUserReport } from "@/types/reportTypes";
import { first, sumBy, some } from "lodash";
import { formatCount } from "@/helpers/formatCount";
import { formatDollar } from "@/helpers/formatDollar";
import AccountSelectModal from "@/components/modules/account/AccountSelectModal.vue";
import AccountSelectSearch from "@/components/modules/account/AccountSelectSearch.vue";
import PositionStatementsModal from "@/components/modules/report/PositionStatementsModal.vue";
import TicketsModal from "@/components/modules/report/TicketsModal.vue";
import StrikeStatementsModal from "@/components/modules/report/StrikeStatementsModal.vue";
import OutsetStrikeStatementsModal from "@/components/modules/report/OutsetStrikeStatementsModal.vue";
import UserBreadcrumb from "@/components/common/UserBreadcrumb.vue";
import { canSeeDdCompanyLevel } from "@/helpers/user/canSeeDdCompanyLevel";
import { FEATURE_TOGGLES } from "@/config";
import CompanyPlTable from "@/components/modules/report/CompanyPlTable.vue";
import { computed, ref } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute, useRouter } from "vue-router/composables";
import { useTranslation } from "@/store/translation";


export default /*#__PURE__*/_defineComponent({
  __name: 'FullReportLegacy',
  setup(__props) {

const showAccountSelectionModal = ref(false);

const route = useRoute();
const router = useRouter();
const { $t } = useTranslation();

const selectedStartReleaseDate = computed(() => {
  const query = route.query.startReleaseDate;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const selectedEndReleaseDate = computed(() => {
  const query = route.query.endReleaseDate;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const { result: meResult, loading: meLoading } = useQuery<{ me: User }>(
  queries.me,
  null,
  {
    fetchPolicy: "cache-first",
  }
);

const me = computed(() => meResult.value?.me);

const { result: fullReportResult, loading: fullReportLoading } = useQuery<{
  fullReport: LegacyUserReport;
}>(queries.fullReportLegacy, {
  releaseDateStart: selectedStartReleaseDate,
  releaseDateEnd: selectedEndReleaseDate,
  userId: computed(() => route.query.userId),
});

const fullReport = computed(() => fullReportResult.value?.fullReport);

const companyReportQuery = FEATURE_TOGGLES.useV1Legacy
  ? queries.companyReportLegacy
  : queries.companyReport;

const { result: companyReportResult, loading: companyReportLoading } =
  useQuery<{
    companyReport: CompanyReport;
  }>(companyReportQuery, {
    releaseDateStart: selectedStartReleaseDate,
    releaseDateEnd: selectedEndReleaseDate,
  });

const { result: userResult, loading: userLoading } = useQuery<{ user: User }>(
  queries.user,
  {
    userId: computed(() => route.query.userId),
  }
);

const loading = computed(() => {
  return (
    meLoading.value ||
    fullReportLoading.value ||
    companyReportLoading.value ||
    userLoading.value
  );
});

const showStrikeModal = ref(false);
const showTicketsModal = ref(false);
const showOutsetStrikeModal = ref(false);
const showPositionModal = ref(false);
const selectedUserId = ref<null | User["userId"]>(null);
const includeDownlines = ref(true);
const selectedExternalCompanyId = ref<null | ExternalCompany["userId"]>(null);
const selectedStatementType = ref<StatementType>(StatementType.TicketIn);

const seeCompanyLevel = computed(() => {
  return canSeeDdCompanyLevel(me.value);
});

const isNotAdminViewingAdmin = computed(() => {
  return route.query?.userId === "admin" && me.value?.userId !== "admin";
});

const selectedUserStatement = computed(() => {
  if (!fullReport.value) {
    return null;
  }
  return [
    fullReport.value.consolidated,
    ...(fullReport.value.downlines ?? []),
  ].find((s) => s.userId === selectedUserId.value);
});

const showCompany = computed(() => {
  return route.query.userId === "admin";
});

const netProfit = computed(() => {
  if (!fullReport.value) {
    return 0;
  }
  const netProfit =
    fullReport.value.consolidated.netBal - downlinesSummary.value.netBal;
  if (Math.abs(netProfit) < 0.001) {
    return 0;
  }
  return netProfit;
});

const companyPlStatement = computed(() => {
  return companyReportResult.value?.companyReport.companyTicketsFinal.summary;
});

const downlinesWithTickets = computed(() => {
  return fullReport.value?.downlines?.filter((dl) =>
    some(
      Object.values(dl).filter((val) => typeof val === "number" && val !== 0)
    )
  );
});

const downlinesSummary = computed(() => {
  return {
    big: sumBy(downlinesWithTickets.value, (dl) => dl.big),
    small: sumBy(downlinesWithTickets.value, (dl) => dl.small),
    amount: sumBy(downlinesWithTickets.value, (dl) => dl.amount),
    rebate: sumBy(downlinesWithTickets.value, (dl) => dl.rebate),
    amountLessRebate: sumBy(
      downlinesWithTickets.value,
      (dl) => dl.amountLessRebate
    ),
    strikeAmount: sumBy(downlinesWithTickets.value, (dl) => dl.strikeAmount),
    strikeCommission: sumBy(
      downlinesWithTickets.value,
      (dl) => dl.strikeCommission
    ),
    net: sumBy(downlinesWithTickets.value, (dl) => dl.net),
    companyStake: sumBy(downlinesWithTickets.value, (dl) => dl.companyStake),
    position: sumBy(downlinesWithTickets.value, (dl) => dl.position),
    netBal: sumBy(downlinesWithTickets.value, (dl) => dl.netBal),
  };
});

function showPosition(userId?: User["userId"]) {
  if (userId) {
    selectedUserId.value = userId;
  }
  showPositionModal.value = true;
}
function showStrike(
  userId: User["userId"] | undefined,
  statementType: StatementType
) {
  if (userId) {
    selectedUserId.value = userId;
  }
  selectedStatementType.value = statementType;
  showStrikeModal.value = true;
}
function showTickets(
  userId: User["userId"] | undefined,
  includeDownlinesVar: boolean
) {
  if (userId) {
    selectedUserId.value = userId;
  }
  includeDownlines.value = includeDownlinesVar;
  showTicketsModal.value = true;
}
function showOutsetStrike(userId: ExternalCompany["userId"]) {
  selectedExternalCompanyId.value = userId;
  showOutsetStrikeModal.value = true;
}

function seeAdmin() {
  router.push({
    name: "full report",
    query: { ...route.query, userId: "admin" },
  });
}

return { __sfc: true,showAccountSelectionModal, route, router, $t, selectedStartReleaseDate, selectedEndReleaseDate, meResult, meLoading, me, fullReportResult, fullReportLoading, fullReport, companyReportQuery, companyReportResult, companyReportLoading, userResult, userLoading, loading, showStrikeModal, showTicketsModal, showOutsetStrikeModal, showPositionModal, selectedUserId, includeDownlines, selectedExternalCompanyId, selectedStatementType, seeCompanyLevel, isNotAdminViewingAdmin, selectedUserStatement, showCompany, netProfit, companyPlStatement, downlinesWithTickets, downlinesSummary, showPosition, showStrike, showTickets, showOutsetStrike, seeAdmin, StatementType, formatCount, formatDollar, AccountSelectModal, AccountSelectSearch, PositionStatementsModal, TicketsModal, StrikeStatementsModal, OutsetStrikeStatementsModal, UserBreadcrumb, FEATURE_TOGGLES, CompanyPlTable }
}

})